import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import { Instagram, LifeBuoy, Mail, MessageCircle, MessagesSquare } from 'lucide-react';

import CONFIG from '../../config/config';
import { Badge } from '../Badge/Badge';
import { useCrispChat } from '../Crisp/CrispProvider';
import { Modal } from '../Modal/Modal';

const contactButtonClass =
  'group flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 font-semibold text-gray-900 transition-colors hover:border-gray-900 hover:bg-gray-900 hover:text-white dark:bg-muted dark:text-foreground dark:hover:bg-accent dark:hover:text-foreground sm:min-w-[10rem]';

const compactContactButtonClass =
  'group flex min-w-0 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm font-semibold text-gray-900 transition-colors hover:border-gray-900 hover:bg-gray-900 hover:text-white dark:bg-muted dark:text-foreground dark:hover:bg-accent dark:hover:text-foreground';

const contactLabelClass =
  'block text-xs font-normal text-gray-500 group-hover:text-white group-hover:opacity-80 dark:text-muted-foreground dark:group-hover:text-foreground';

const compactContactLabelClass =
  'block text-[11px] font-normal text-gray-500 group-hover:text-white group-hover:opacity-80 dark:text-muted-foreground dark:group-hover:text-foreground';

function formatUnread(count: number): string {
  return count > 9 ? '9+' : String(count);
}

function HelpContactDialog() {
  const { t } = useTranslation();
  const isLoggedIn = useSelector((state: any) => state.settings.value.isLoggedIn);
  const { openChat, unreadCount } = useCrispChat();
  const [isOpen, setIsOpen] = useState(false);

  const buttonLabel =
    unreadCount > 0
      ? t('help.chatUnread', { count: unreadCount })
      : t('help.button');
  const professionalPlanName = t('pricing.advancedStore.name');

  const openModal = () => {
    datadogRum.addAction('Help Contact - Opened');
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  const handlePlanClick = () => {
    datadogRum.addAction('Help Contact - Professional Plan Clicked');
    closeModal();
  };

  const handleChat = () => {
    datadogRum.addAction('Help Contact - Chat Clicked');
    closeModal();
    openChat();
  };

  const handleWhatsApp = () => {
    datadogRum.addAction('Help Contact - WhatsApp Clicked');
    window.open(CONFIG.CONTACT_INFO.WHATSAPP, '_blank', 'noopener,noreferrer');
  };

  const handleEmail = () => {
    datadogRum.addAction('Help Contact - Email Clicked');
    window.location.href = `mailto:${CONFIG.CONTACT_INFO.EMAIL}`;
  };

  const handleInstagram = () => {
    datadogRum.addAction('Help Contact - Instagram Clicked');
    window.open(CONFIG.CONTACT_INFO.INSTAGRAM, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    if (isOpen) {
      datadogRum.addAction('Help Contact - Modal Viewed');
    }
  }, [isOpen]);

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        aria-label={buttonLabel}
        title={buttonLabel}
        data-dd-action-name="Help Contact Button"
        className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-2.5 py-1.5 text-white shadow-lg transition hover:scale-105 hover:bg-gray-800 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 dark:bg-muted dark:text-foreground dark:shadow-none dark:hover:scale-100 dark:hover:bg-accent dark:hover:shadow-none dark:focus-visible:ring-offset-background"
      >
        <span className="relative inline-flex">
          <LifeBuoy className="h-4 w-4 shrink-0" aria-hidden />
          {unreadCount > 0 ? (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs p-0"
              aria-hidden
            >
              {formatUnread(unreadCount)}
            </Badge>
          ) : null}
        </span>
        <span className="hidden text-sm font-medium md:inline">{t('help.button')}</span>
      </button>

      {isOpen && (
        <Modal modalTitle="help.modal.title" canClose onClose={closeModal}>
          <div className="space-y-6">
            <div className="space-y-3">
              <p className="text-gray-700 leading-relaxed">{t('help.modal.intro')}</p>
              <ul className="list-disc space-y-1 pl-5 text-sm text-gray-600">
                <li>{t('help.modal.example.technical')}</li>
                <li>{t('help.modal.example.setup')}</li>
                <li>{t('help.modal.example.module')}</li>
                <li>{t('help.modal.example.services')}</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">{t('help.modal.intro_closing')}</p>
            </div>
            <p className="text-gray-600 text-sm">{t('help.modal.language')}</p>

            <div className="flex flex-col gap-4">
              {isLoggedIn && (
                <button
                  type="button"
                  onClick={handleChat}
                  aria-label={
                    unreadCount > 0
                      ? String(t('help.chatUnread', { count: unreadCount }))
                      : String(t('help.askAnything'))
                  }
                  className="inline-flex w-full items-center justify-center gap-3 rounded-lg bg-gray-900 px-4 py-3 font-semibold text-white transition hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
                >
                  <span className="relative inline-flex">
                    <MessagesSquare className="h-6 w-6 shrink-0" aria-hidden />
                    {unreadCount > 0 ? (
                      <Badge
                        variant="destructive"
                        className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs p-0"
                        aria-hidden
                      >
                        {formatUnread(unreadCount)}
                      </Badge>
                    ) : null}
                  </span>
                  <span className="text-left">
                    <span className="block text-xs font-normal text-white/70 dark:text-primary-foreground/70">{t('help.chat')}</span>
                    <span>{t('help.askAnything')}</span>
                  </span>
                </button>
              )}

              <div
                className={
                  isLoggedIn
                    ? 'grid grid-cols-1 gap-2 border-t border-gray-200 pt-4 sm:grid-cols-3'
                    : 'flex flex-col gap-3 sm:flex-row sm:flex-wrap'
                }
              >
                <button
                  type="button"
                  onClick={handleWhatsApp}
                  className={isLoggedIn ? compactContactButtonClass : contactButtonClass}
                >
                  <MessageCircle className="h-5 w-5 shrink-0" aria-hidden />
                  <span className="min-w-0 text-left">
                    <span className={isLoggedIn ? compactContactLabelClass : contactLabelClass}>
                      {t('help.modal.whatsapp')}
                    </span>
                    <span className="block truncate">{CONFIG.CONTACT_INFO.PHONE}</span>
                  </span>
                </button>

                <button
                  type="button"
                  onClick={handleEmail}
                  className={isLoggedIn ? compactContactButtonClass : contactButtonClass}
                >
                  <Mail className="h-5 w-5 shrink-0" aria-hidden />
                  <span className="min-w-0 text-left">
                    <span className={isLoggedIn ? compactContactLabelClass : contactLabelClass}>
                      {t('help.modal.email')}
                    </span>
                    <span className="block truncate">{CONFIG.CONTACT_INFO.EMAIL}</span>
                  </span>
                </button>

                <button
                  type="button"
                  onClick={handleInstagram}
                  className={isLoggedIn ? compactContactButtonClass : contactButtonClass}
                >
                  <Instagram className="h-5 w-5 shrink-0" aria-hidden />
                  <span className="min-w-0 text-left">
                    <span className={isLoggedIn ? compactContactLabelClass : contactLabelClass}>
                      {t('help.modal.instagram')}
                    </span>
                    <span className="block truncate">@eateliers</span>
                  </span>
                </button>
              </div>
            </div>

            <div className="rounded-lg border-l-4 border-amber-600 bg-amber-50 p-4 text-sm text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
              <Trans
                i18nKey="help.modal.professional_priority"
                values={{ plan: professionalPlanName }}
                components={{
                  planName: (
                    <Link
                      to="/subscription"
                      onClick={handlePlanClick}
                      className="font-semibold text-amber-700 underline-offset-2 hover:underline dark:text-amber-300"
                    />
                  ),
                }}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export default HelpContactDialog;
